import styled from 'styled-components';
import { Typography } from '../typography';
import { Chip } from '../chip';
import { Card, CardHeader } from './Components';
import { colors } from '../../const';

interface OperationPanelProps {
  title: string;
  viewAllLink?: string;
  data: {
    name: string;
    content: string;
    value: number;
    icon: React.ReactNode;
    withoutBg?: boolean;
    color: string;
  }[];
}

const themeColors = {
  lightblue: { main: colors['--primary-40'], contrastText: colors['--primary-100'] },
  lightgreen: { main: colors['--green-40'], contrastText: colors['--green-100'] },
  lightyellow: { main: colors['--yellow-40'], contrastText: colors['--yellow-100'] },
  lightred: { main: colors['--red-40'], contrastText: colors['--red-100'] },
};

const List = styled.div`
  display: grid;
  row-gap: 1.2rem;

  & > *:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  column-gap: 1.2rem;
  align-items: center;

  padding-bottom: 1.2rem;
  border-bottom: 0.1rem solid ${colors['--gray-50']};

  & > .MuiChip-root {
    min-width: 4rem;
  }
`;

const IconWrap = styled.div<{ $bg: string; $fg: string; $withoutBg?: boolean }>`
  width: 4rem;
  height: 4rem;
  border-radius: var(--border-radius-6);
  background: ${({ $bg, $withoutBg }) => ($withoutBg ? colors['--transparent'] : $bg)};
  display: grid;
  place-items: center;

  svg {
    font-size: ${({ $withoutBg }) => ($withoutBg ? '3.2rem' : '2.2rem')};
    color: ${({ $fg }) => $fg};
  }
`;

const Texts = styled.div`
  display: grid;
  row-gap: 0.2rem;
  min-width: 0;
`;

export const OperationPanel = ({ title, data, viewAllLink }: OperationPanelProps) => {
  return (
    <Card>
      <CardHeader title={title} viewAllLink={viewAllLink} />

      <List>
        {data.map((item, idx) => {
          const palette =
            themeColors[item.color as keyof typeof themeColors] ||
            ({
              main: colors['--gray-30'],
              contrastText: colors['--gray-80'],
            } as const);

          return (
            <Row key={`${item.name}-${idx}`}>
              <IconWrap $withoutBg={item.withoutBg} $bg={palette.main} $fg={palette.contrastText}>
                {item.icon}
              </IconWrap>
              <Texts>
                <Typography
                  variant="textS"
                  weight="semibold"
                  color="--gray-90"
                  style={{ margin: 0 }}
                >
                  {item.name}
                </Typography>
                <Typography variant="textS" weight="normal" color="--gray-70" style={{ margin: 0 }}>
                  {item.content}
                </Typography>
              </Texts>
              <Chip label={String(item.value)} color={item.color as any} />
            </Row>
          );
        })}
      </List>
    </Card>
  );
};
